import { useCallback } from 'react';
import get from 'lodash/get';
import { Text, TextCell, type TextProps } from '@direct-frontend/components';
import { type BaseRowData } from '@adv-frontend/data-provider';
import { type AttributeOptions } from './lib/types';
import { EMPTY_DASH } from '../../../../lib/constants';
import { format } from '../../../../lib/string';
import { type ExtendCellTemplateProps, type GridRow } from '../../lib/types';

export interface CellAttributeValueProps<RowData extends BaseRowData = GridRow>
    extends ExtendCellTemplateProps<RowData>,
        AttributeOptions {}

export const CellAttributeValue = <RowData extends BaseRowData = GridRow>({
    'data-testid': dataTestId = 'CellAttributeValue',
    name,
    type,
    formatOptions,
    isTruncated,
    align,
    data,
    onClick,
    className,
}: CellAttributeValueProps<RowData>) => {
    const title = useCallback(
        (textProps: TextProps) => {
            const originalValue = get(data, name);
            let value = originalValue;
            let hasValue = originalValue !== undefined && originalValue !== null;

            if (type) {
                value = format(originalValue, type, { ...formatOptions, invalidValue: false });
                hasValue = Boolean(format(originalValue, type, { ...formatOptions, invalidValue: false }));
            }

            return (
                <Text {...textProps} role={hasValue ? textProps.role : 'supplementary'}>
                    {hasValue ? value : EMPTY_DASH}
                </Text>
            );
        },
        [data, formatOptions, name, type]
    );

    return (
        <TextCell
            isTruncated={isTruncated}
            data-testid={dataTestId}
            className={className}
            onClick={onClick}
            title={title}
            align={align}
        />
    );
};
